import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Bugsnag from "@bugsnag/js";

import api from "api";
import styles from "./ReferralBlock.module.scss";
import ReferralImage from "../../assets/images/assetStarterPack.webp";

/**
 * `/account/referral-code` returns `{ code, credit_reward }`, where the reward
 * is the per-order credit — the headline is that credit across the two orders
 * it applies to, which is where "Give £10. Get £10." off a £5 reward comes
 * from. The endpoint is account-scoped, so the block only exists for a
 * signed-in customer and renders nothing until a code comes back.
 */
const ReferralBlock = ({ className }) => {
  const { isLoggedIn } = useSelector((s) => s.auth);
  const [referral, setReferral] = useState(null);
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }

    let cancelled = false;

    api.get("/account/referral-code").then(
      ({ data }) => {
        if (!cancelled && data?.code) {
          setReferral(data);
        }
      },
      // A missing referral code isn't an error worth surfacing to the
      // customer — the block just doesn't appear.
      () => {},
    );

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const copy = useCallback(() => {
    if (!referral?.code) {
      return;
    }

    navigator.clipboard.writeText(referral.code).then(() => {
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    }, Bugsnag.notify);
  }, [referral]);

  if (!referral?.code) {
    return null;
  }

  const reward = referral.credit_reward;
  const rewardValue = parseFloat(reward?.amount ?? 0);

  const money = (value) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: reward?.currency || "GBP",
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);

  const single = money(rewardValue);
  const doubled = money(rewardValue * 2);

  return (
    <div className={`${styles.container} ${className || ""}`}>
      <img className={styles.image} alt="" src={ReferralImage} />

      <div className={styles.content}>
        <p className="gkit-p-17 gkit-mobile-15 gkit-bold gkit-mt-0 gkit-mb-2">
          {`Give ${doubled}. Get ${doubled}.`}
        </p>

        <p className="gkit-p-15 gkit-mobile-13 gkit-my-0">
          {`Introduce a friend to Grind and when they use your code, they'll get ${single} off their first two subscription orders.`}
        </p>

        <p className="gkit-p-15 gkit-mobile-13 gkit-mt-3 gkit-mb-0">
          {`We'll also give you ${single} off your next two subscription orders too!`}
        </p>

        <div className={styles.codeRow}>
          <label className={styles.srOnly} htmlFor="referral-code">
            Your referral code
          </label>
          <input
            className={`gkit-input-md ${styles.code}`}
            id="referral-code"
            readOnly
            onFocus={(event) => event.target.select()}
            value={referral.code}
          />

          {navigator.clipboard && (
            <button
              className={`gkit-btn-md gkit-primary ${styles.copy}`}
              onClick={copy}
              type="button"
            >
              {hasCopied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralBlock;
